import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "@/lib/api-client";
import type { CrmEventActionIntent, CrmEventActionPriority } from "./use-crm-event-action-score";

export interface CrmEventActionContractSampleRecommendation {
  id: string;
  actionRef: string;
  intent: CrmEventActionIntent | string;
  priority: CrmEventActionPriority;
  confidence: number;
  requiresHumanReview: boolean;
}

export interface CrmEventActionContractTelemetry {
  eventType: string;
  reviewed: number;
  accepted: number;
  dismissed: number;
  materialized: number;
  lastReviewedAt: string | null;
  lastAcceptedAt: string | null;
  lastMaterializedAt: string | null;
  topActionRefs: Array<{ actionRef: string; accepted: number; materialized: number }>;
}

export interface CrmEventActionContract {
  signalKey: string;
  signalFamily: string;
  eventType: string;
  entityType: string;
  description: string;
  activityType: string | null;
  leadScoreWeightKey: string | null;
  scoreDelta: number;
  samplePayloadKeys: string[];
  recommendationBacked: boolean;
  recommendationCount: number;
  humanReviewRecommendationCount: number;
  actionRefs: string[];
  intents: Array<CrmEventActionIntent | string>;
  priorities: CrmEventActionPriority[];
  queueMaterializableActionRefs: string[];
  manualOnlyActionRefs: string[];
  queueActionable: boolean;
  tenantTelemetry: CrmEventActionContractTelemetry;
  sampleRecommendations: CrmEventActionContractSampleRecommendation[];
}

export interface CrmEventActionContractSummary {
  registeredEvents: number;
  recommendationBackedEvents: number;
  queueActionableEvents: number;
  telemetryBackedEvents: number;
  uncoveredEvents: number;
  acceptedBySourceEvents: number;
  materializedBySourceEvents: number;
  totalSampleRecommendations: number;
  eventCoverageRate: number;
  queueActionableCoverageRate: number;
  signalFamilies: string[];
  uniqueActionRefs: string[];
  uniqueIntents: string[];
}

export interface CrmEventActionContractsResponse {
  contractVersion: "stero.crm_event_action_contracts.v1";
  windowDays: number;
  filters: {
    sourceEventType: string | null;
    signalFamily: string | null;
  };
  summary: CrmEventActionContractSummary;
  contracts: CrmEventActionContract[];
  safety: {
    staticRegistry: true;
    executesActions: false;
    mutatesCrm: false;
    usesSyntheticSamplePayloads: true;
  };
}

export interface CrmEventActionContractsFilters {
  sourceEventType?: string;
  signalFamily?: string;
}

export function useCrmEventActionContracts(filters: CrmEventActionContractsFilters = {}) {
  const [data, setData] = useState<CrmEventActionContractsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContracts = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (filters.sourceEventType) {
        params.set("sourceEventType", filters.sourceEventType);
      }
      if (filters.signalFamily) {
        params.set("signalFamily", filters.signalFamily);
      }
      const query = params.toString();
      const response = await apiFetch<{ data: CrmEventActionContractsResponse }>(
        `/crm/intelligence/event-action-score/contracts${query ? `?${query}` : ""}`,
      );
      setData(response.data);
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : "Failed to load event action contracts");
    } finally {
      setIsLoading(false);
    }
  }, [filters.signalFamily, filters.sourceEventType]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  return { data, isLoading, error, refetch: fetchContracts };
}
